import type { OnboardingState, UserPersona } from '@ddp/shared';

import type { MessageKey } from '../i18n/index.js';

/**
 * Visites guidées (ADR-0031). Une visite = une suite d'étapes, chacune ancrée sur un élément réel
 * repéré par son attribut `data-tour`. Le texte vient **toujours** de clés i18n (`tour.*`), comme
 * `HelpHint`. Chaque visite porte une **version** : monter la version d'une visite déjà vue la
 * re-signale une fois (le cas « une nouveauté sur une section déjà connue », ADR-0031 §4).
 */

/** Côté préféré d'ancrage de la bulle par rapport à sa cible. */
export type TourPlacement = 'right' | 'bottom' | 'top' | 'left';

export interface TourStep {
  /** Valeur de l'attribut `data-tour` de l'élément ancre. */
  target: string;
  titleKey: MessageKey;
  textKey: MessageKey;
  placement?: TourPlacement;
}

export interface TourDef {
  version: number;
  steps: TourStep[];
  /**
   * `true` si les étapes s'ancrent sur le **rail de navigation** (masqué sur mobile). La coquille
   * ouvre alors le tiroir pendant la visite pour que les cibles soient visibles.
   */
  anchorsRail?: boolean;
}

/**
 * Catalogue des visites. Incrément 1 : seule `overview` est définie (elle présente les quatre
 * sections + l'aide). Les visites `data`/`model`/`deploy` arriveront à l'incrément 2 — la mécanique
 * de file et de version les accueillera sans changement.
 */
export const TOURS: Record<string, TourDef> = {
  overview: {
    version: 1,
    anchorsRail: true,
    steps: [
      {
        target: 'nav-overview',
        titleKey: 'tour.overview.overview.title',
        textKey: 'tour.overview.overview.text',
        placement: 'right',
      },
      {
        target: 'nav-data',
        titleKey: 'tour.overview.data.title',
        textKey: 'tour.overview.data.text',
        placement: 'right',
      },
      {
        target: 'nav-model',
        titleKey: 'tour.overview.model.title',
        textKey: 'tour.overview.model.text',
        placement: 'right',
      },
      {
        target: 'nav-deploy',
        titleKey: 'tour.overview.deploy.title',
        textKey: 'tour.overview.deploy.text',
        placement: 'right',
      },
      {
        target: 'account',
        titleKey: 'tour.overview.account.title',
        textKey: 'tour.overview.account.text',
        placement: 'right',
      },
    ],
  },
  // Visites de section (incrément 2). Elles s'ancrent sur des éléments **déjà présents** de chaque
  // page (via leur `id` : le moteur retombe sur `#id` faute d'attribut `data-tour`), donc sans
  // toucher aux pages. Déclenchées à la visite de la page (l'ancre doit être montée).
  data: {
    version: 1,
    steps: [
      {
        target: 'data-videos',
        titleKey: 'tour.data.videos.title',
        textKey: 'tour.data.videos.text',
        placement: 'bottom',
      },
      {
        target: 'data-images',
        titleKey: 'tour.data.images.title',
        textKey: 'tour.data.images.text',
        placement: 'bottom',
      },
    ],
  },
  model: {
    version: 1,
    steps: [
      {
        target: 'model-import',
        titleKey: 'tour.model.import.title',
        textKey: 'tour.model.import.text',
        placement: 'bottom',
      },
      {
        target: 'model-registry',
        titleKey: 'tour.model.registry.title',
        textKey: 'tour.model.registry.text',
        placement: 'bottom',
      },
    ],
  },
  deploy: {
    version: 1,
    steps: [
      {
        target: 'deploy-compute',
        titleKey: 'tour.deploy.compute.title',
        textKey: 'tour.deploy.compute.text',
        placement: 'bottom',
      },
      {
        target: 'deploy-hardware',
        titleKey: 'tour.deploy.hardware.title',
        textKey: 'tour.deploy.hardware.text',
        placement: 'bottom',
      },
    ],
  },
};

/**
 * Portée de visite associée à une route de section (incrément 2). `null` hors de ces pages ; la
 * visite `overview`, ancrée sur le rail, ne dépend d'aucune page et n'apparaît donc pas ici.
 */
export function scopeForPath(pathname: string): string | null {
  if (pathname === '/data' || pathname.startsWith('/data/')) return 'data';
  if (pathname === '/model') return 'model';
  if (pathname === '/deploy') return 'deploy';
  return null;
}

/** Onglet d'accueil par défaut de chaque persona (effet « doux » : redirection, rien de masqué). */
export const PERSONA_LANDING: Record<UserPersona, string> = {
  GENERALIST: '/',
  COLLECTOR: '/data',
  AI_ENGINEER: '/model',
  FLEET_MANAGER: '/deploy',
  OPERATOR: '/ops',
};

/**
 * `true` si la visite `scope` doit se déclencher : jamais vue, ou vue dans une version antérieure
 * (une montée de version re-signale une nouveauté sur une section déjà connue, ADR-0031 §4).
 *
 * Le déclenchement est **contextuel**, pas piloté par le persona : `overview` s'ouvre à la première
 * connexion, et chaque visite de section s'ouvre la première fois que l'utilisateur visite la page
 * concernée (d'où « la nouveauté Deploy » qui apparaît au gestionnaire quand il ouvre Deploy). Le
 * persona, lui, ne décide que de l'onglet d'accueil (effet « doux »).
 */
export function tourIsPending(scope: string, state: OnboardingState): boolean {
  const def = TOURS[scope];
  if (!def) return false;
  const seen = state[scope];
  return !seen || seen.version < def.version;
}
